import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import './OrganizationModals.css';

const CreateSalesmanModal = ({ show, onClose, onSuccess, branches }) => {
    const [formData, setFormData] = useState({
        employeeId: '',
        name: '',
        branchId: '',
        phone: '',
        email: ''
    });
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show) {
            // Reset form when modal opens
            setFormData({
                employeeId: '',
                name: '',
                branchId: '',
                phone: '',
                email: ''
            });
            setPhotoFile(null);
            setPhotoPreview(null);
            setError(null);
        }
    }, [show]);

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError('Please select an image file');
                return;
            }
            // Validate file size (2MB)
            if (file.size > 2 * 1024 * 1024) {
                setError('Photo size must be less than 2MB');
                return;
            }
            setPhotoFile(file);
            setPhotoPreview(URL.createObjectURL(file));
            setError(null);
        }
    };

    const handleRemovePhoto = () => {
        setPhotoFile(null);
        if (photoPreview) {
            URL.revokeObjectURL(photoPreview);
        }
        setPhotoPreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                employeeId: formData.employeeId.trim().toUpperCase(),
                name: formData.name.trim(),
                branchId: formData.branchId,
                phone: formData.phone.trim(),
                email: formData.email.trim().toLowerCase()
            };

            const response = await axiosInstance.post('/api/sales-target/salesmen', payload);

            if (response.data.success) {
                const salesmanId = response.data.data._id;
                
                // Upload photo if provided
                if (photoFile) {
                    try {
                        const photoFormData = new FormData();
                        photoFormData.append('photo', photoFile);
                        await axiosInstance.post(`/api/sales-target/salesmen/${salesmanId}/photo`, photoFormData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                        });
                    } catch (photoErr) {
                        console.error('Photo upload failed:', photoErr);
                        // Don't fail the whole operation if photo upload fails
                    }
                }
                
                onSuccess();
                handleClose();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create salesman');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({
                employeeId: '',
                name: '',
                branchId: '',
                phone: '',
                email: ''
            });
            handleRemovePhoto();
            setError(null);
            onClose();
        }
    };

    if (!show) return null;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Add New Salesman</h2>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Photo (Optional)</label>
                        <div className="photo-upload-section">
                            {photoPreview ? (
                                <div className="photo-preview-container">
                                    <img src={photoPreview} alt="Preview" className="photo-preview" />
                                    <button 
                                        type="button" 
                                        onClick={handleRemovePhoto} 
                                        className="btn-remove-photo"
                                        disabled={loading}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ) : (
                                <div className="photo-upload-placeholder">
                                    <span>📷</span>
                                    <p>No photo selected</p>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoChange}
                                disabled={loading}
                                id="photo-upload"
                                style={{ display: 'none' }}
                            />
                            <label htmlFor="photo-upload" className="btn-upload-photo">
                                {photoPreview ? 'Change Photo' : 'Upload Photo'}
                            </label>
                        </div>
                        <small>JPG, PNG, or GIF (max 2MB)</small>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Employee ID *</label>
                            <input
                                type="text"
                                value={formData.employeeId}
                                onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                                placeholder="e.g., B1001, EMP001"
                                required
                                disabled={loading}
                                style={{ textTransform: 'uppercase' }}
                            />
                            <small>Unique identifier from your ERP system</small>
                        </div>

                        <div className="form-group">
                            <label>Full Name *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Enter full name"
                                required
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Branch *</label>
                        <select
                            value={formData.branchId}
                            onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                            required
                            disabled={loading}
                        >
                            <option value="">-- Select Branch --</option>
                            {branches.map(branch => (
                                <option key={branch._id} value={branch._id}>
                                    {branch.name}
                                </option>
                            ))}
                        </select>
                        <small>Branch where this salesman will be assigned</small>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Phone</label>
                            <input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="+974-12345678"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label>Email</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="salesman@example.com"
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="info-box">
                        <strong>Note:</strong> Employee ID must be unique across all branches. 
                        Phone and email are optional but recommended for better communication.
                    </div>

                    <div className="modal-actions">
                        <button 
                            type="button" 
                            onClick={handleClose} 
                            className="btn-secondary" 
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="btn-primary" 
                            disabled={loading || !formData.employeeId || !formData.name || !formData.branchId}
                        >
                            {loading ? 'Creating...' : 'Create Salesman'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateSalesmanModal;